'use client';

import clsx from 'clsx';
import type { ForwardedRef } from 'react';
import { Switch as RACSwitch, type SwitchProps as RACSwitchProps } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { IconCheckBold, IconCloseBold } from '../../icons/index.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import type { AriaLabelingProps, CommonProps, StringLikeChildren } from '../types.js';
import { Text } from '../typography/text.js';

export interface UNSTABLE_ToggleProps
  extends
    CommonProps,
    AriaLabelingProps,
    Pick<
      RACSwitchProps,
      | 'onChange'
      | 'isSelected'
      | 'isDisabled'
      | 'isReadOnly'
      | 'defaultSelected'
      | 'autoFocus'
      | 'form'
      | 'name'
      | 'value'
    > {
  /** The label rendered next to the toggle. */
  children?: StringLikeChildren;
}

function UNSTABLE_Toggle(
  { children, UNSAFE_className, UNSAFE_style, ...props }: UNSTABLE_ToggleProps,
  ref: ForwardedRef<HTMLLabelElement>,
) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!children && !ariaLabel && !ariaLabelledBy) {
      console.warn('Toggle requires one of children / aria-label / aria-labelledby for accessibility');
    }
  }, [children, ariaLabel, ariaLabelledBy]);

  return (
    <RACSwitch {...props} ref={ref} className={clsx('cim-toggle', UNSAFE_className)} style={UNSAFE_style}>
      <div className="cim-toggle-track-wrapper" aria-hidden>
        <div className="cim-toggle-track">
          <IconCheckBold />
          <IconCloseBold />
          <div className="cim-toggle-track-head" />
        </div>
      </div>
      {children && (
        <Text as="span" variant="medium">
          {children}
        </Text>
      )}
    </RACSwitch>
  );
}

/**
 * Allows users to turn a setting on or off.
 *
 * See [toggle usage guidelines](https://ui.cimpress.io/components/toggle/).
 */
const _UNSTABLE_Toggle = withStyleProps(forwardRef(UNSTABLE_Toggle), 'Toggle');

export { _UNSTABLE_Toggle as UNSTABLE_Toggle };
